import { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { AlertCircle, Check, CheckCircle, Copy, HardDrive, Loader, Monitor, Smartphone, TerminalSquare, Wifi, X } from 'lucide-react'
import { SEO } from '../components/SEO'
import { API_ENDPOINTS, request } from '../config/api'
import { useLanguage } from '../contexts/LanguageContext'
import { t } from '../locales/translations'

function hasReleasePayload(value) {
  if (!value || typeof value !== 'object') {
    return false
  }
  return ['windows', 'linux', 'android', 'macos'].some((platform) => {
    if (!(platform in value)) {
      return false
    }
    const item = value[platform]
    return item == null || typeof item === 'object'
  })
}

function formatReleaseDate(value) {
  if (!value) {
    return '--'
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }
  return parsed.toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-')
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

const DownloadPage = () => {
  const { language } = useLanguage()
  const [releaseMap, setReleaseMap] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [forceRefresh, setForceRefresh] = useState(false)
  const [installPlatform, setInstallPlatform] = useState(null)
  const [copyStatus, setCopyStatus] = useState('idle')
  const copyResetTimerRef = useRef(null)

  useEffect(() => {
    const CACHE_KEY = 'avelia_node_release_all'
    const CACHE_DURATION = 10 * 60 * 5

    const fetchVersion = async () => {
      try {
        setLoading(true)
        setError(null)

        if (!forceRefresh) {
          const cachedData = localStorage.getItem(CACHE_KEY)
          if (cachedData) {
            try {
              const { data, timestamp } = JSON.parse(cachedData)
              if (Date.now() - timestamp <= CACHE_DURATION && hasReleasePayload(data)) {
                setReleaseMap(data)
                setLoading(false)
                return
              }
            } catch (cacheError) {
              localStorage.removeItem(CACHE_KEY)
            }
          }
        } else {
          setForceRefresh(false)
        }

        const result = await request(`${API_ENDPOINTS.GET_LATEST_VERSION}?platform=all`)
        if (hasReleasePayload(result)) {
          setReleaseMap(result)
          setError(null)
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: result,
            timestamp: Date.now()
          }))
        } else {
          setReleaseMap(null)
          setError(language === 'zh' ? '获取版本信息失败' : 'Failed to load release info')
        }
      } catch (requestError) {
        setReleaseMap(null)
        setError(language === 'zh' ? '网络错误，请稍后重试' : 'Network error, please try again later')
      } finally {
        setLoading(false)
      }
    }

    fetchVersion()
  }, [forceRefresh, language])

  const requirements = [
    { icon: Monitor, key: 'os' },
    { icon: HardDrive, key: 'storage' },
    { icon: Wifi, key: 'network' },
  ]

  const steps = [
    { step: 1, key: 'step1' },
    { step: 2, key: 'step2' },
    { step: 3, key: 'step3' },
    { step: 4, key: 'step4' },
  ]

  const windowsRelease = releaseMap?.windows ?? null
  const linuxRelease = releaseMap?.linux ?? null
  const androidRelease = releaseMap?.android ?? null
  const macosRelease = releaseMap?.macos ?? null
  const releaseNotesItems = releaseNotesFromReleaseMap(releaseMap, language)

  const installCommandMap = useMemo(() => ({
    windows: {
      title: language === 'zh' ? 'Windows 快速安装' : 'Windows quick install',
      subtitle: language === 'zh'
        ? '通过 PowerShell 拉取并执行节点安装脚本。'
        : 'Pull and execute the node installer through PowerShell.',
      command: 'irm "https://download.cdn.avelia.chat/script/windows.ps1?t=$(Get-Date -UFormat %s)" | iex',
      notes: language === 'zh'
        ? [
            '# 使用 PowerShell 拉取并执行安装脚本',
            '# 建议在管理员 PowerShell 中执行',
            '# 安装完成后会自动启动 GUI，并在界面内输入绑定码',
          ]
        : [
            '# Pull and execute the installer through PowerShell',
            '# Run this in an elevated PowerShell session',
            '# The GUI will launch automatically after install so you can enter the bind code',
          ],
    },
    linux: {
      title: language === 'zh' ? 'Linux 快速安装' : 'Linux quick install',
      subtitle: language === 'zh'
        ? '通过 curl 获取 Linux 节点安装脚本并直接执行。'
        : 'Fetch the Linux installer with curl and execute it directly.',
      command: 'curl -fsSL "https://download.cdn.avelia.chat/script/linux.sh?t=$(date +%s)" | bash',
      notes: language === 'zh'
        ? [
            '# 下载并执行 Linux 节点安装脚本',
            '# 建议在具备 sudo 权限的终端中执行',
            '# 安装过程中会要求输入绑定码，并自动完成 systemd 服务配置',
          ]
        : [
            '# Download and run the Linux node installer',
            '# Recommended in a terminal with sudo access',
            '# The script will ask for the bind code and finish the systemd setup automatically',
          ],
    },
  }), [language])

  const platformCards = language === 'zh'
    ? [
        {
          key: 'windows',
          icon: Monitor,
          title: 'Windows 节点',
          status: windowsRelease?.download_url ? '稳定通道' : '规划中',
          summary: '面向桌面自动化、官网开发、工作区协作与本机执行。',
          release: windowsRelease,
          supportsQuickInstall: true,
          detail: windowsRelease
            ? `当前同步版本 ${windowsRelease.version}`
            : '当前同步版本信息将在稳定通道返回后展示。',
        },
        {
          key: 'linux',
          icon: TerminalSquare,
          title: 'Linux 节点',
          status: linuxRelease?.download_url ? '稳定通道' : '规划中',
          summary: '面向远程环境接入、服务巡检、命令执行与基础设施协作。',
          release: linuxRelease,
          supportsQuickInstall: true,
          detail: linuxRelease
            ? `当前同步版本 ${linuxRelease.version}`
            : '将作为服务器和远程项目环境的执行入口接入同一调度链。',
        },
        {
          key: 'android',
          icon: Smartphone,
          title: 'Android 节点',
          status: androidRelease?.download_url ? '稳定通道' : '规划中',
          summary: '面向移动端任务、界面操作、表单填写与跨端自动化。',
          release: androidRelease,
          supportsQuickInstall: false,
          detail: androidRelease
            ? `当前同步版本 ${androidRelease.version}`
            : '将让移动设备成为可观察、可操作、可回传结果的执行节点。',
        },
        {
          key: 'macos',
          icon: Monitor,
          title: 'macOS 节点',
          status: macosRelease?.download_url ? '稳定通道' : '规划中',
          summary: '面向 Apple 桌面环境接入、创作协作与本机执行入口。',
          release: macosRelease,
          supportsQuickInstall: false,
          detail: macosRelease
            ? `当前同步版本 ${macosRelease.version}`
            : '将补齐 macOS 桌面端的节点接入与执行链路。',
        }
      ]
    : [
        {
          key: 'windows',
          icon: Monitor,
          title: 'Windows Node',
          status: windowsRelease?.download_url ? 'Stable channel' : 'Planned',
          summary: 'For desktop automation, web projects, workspace collaboration, and local execution.',
          release: windowsRelease,
          supportsQuickInstall: true,
          detail: windowsRelease
            ? `Current synced release ${windowsRelease.version}`
            : 'Stable channel data will appear here when version information is available.',
        },
        {
          key: 'linux',
          icon: TerminalSquare,
          title: 'Linux Node',
          status: linuxRelease?.download_url ? 'Stable channel' : 'Planned',
          summary: 'For remote environments, service checks, command execution, and infrastructure work.',
          release: linuxRelease,
          supportsQuickInstall: true,
          detail: linuxRelease
            ? `Current synced release ${linuxRelease.version}`
            : 'Designed as the execution layer for servers and remote project environments.',
        },
        {
          key: 'android',
          icon: Smartphone,
          title: 'Android Node',
          status: androidRelease?.download_url ? 'Stable channel' : 'Planned',
          summary: 'For mobile tasks, UI operations, form handling, and cross-end automation.',
          release: androidRelease,
          supportsQuickInstall: false,
          detail: androidRelease
            ? `Current synced release ${androidRelease.version}`
            : 'Turns mobile devices into observable, operable, result-returning execution nodes.',
        },
        {
          key: 'macos',
          icon: Monitor,
          title: 'macOS Node',
          status: macosRelease?.download_url ? 'Stable channel' : 'Planned',
          summary: 'For Apple desktop environments, creative collaboration, and local execution.',
          release: macosRelease,
          supportsQuickInstall: false,
          detail: macosRelease
            ? `Current synced release ${macosRelease.version}`
            : 'Completes the desktop-side node entry for macOS environments.',
        }
      ]

  const activeInstallCard = installPlatform ? installCommandMap[installPlatform] : null
  const dualActionDownloadLabel = language === 'zh' ? '立即下载' : 'Download'
  const quickInstallLabel = language === 'zh' ? '快速安装' : 'Quick Install'
  const copyButtonLabel = copyStatus === 'success'
    ? (language === 'zh' ? '已复制' : 'Copied')
    : copyStatus === 'error'
      ? (language === 'zh' ? '复制失败' : 'Copy failed')
      : (language === 'zh' ? '复制' : 'Copy')

  useEffect(() => {
    if (!installPlatform) {
      setCopyStatus('idle')
    }
  }, [installPlatform])

  useEffect(() => () => {
    if (copyResetTimerRef.current) {
      clearTimeout(copyResetTimerRef.current)
    }
  }, [])

  const handleCopyInstallCommand = async () => {
    if (!activeInstallCard) {
      return
    }

    try {
      await copyTextToClipboard(activeInstallCard.command)
      setCopyStatus('success')
    } catch (copyError) {
      setCopyStatus('error')
    }

    if (copyResetTimerRef.current) {
      clearTimeout(copyResetTimerRef.current)
    }
    copyResetTimerRef.current = setTimeout(() => {
      setCopyStatus('idle')
    }, 1800)
  }

  return (
    <>
      <SEO />
      <div className="pb-20 pt-32">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16 text-center"
          >
            <div className="mb-5 inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-4 py-2 text-xs uppercase tracking-[0.22em] text-primary">
              {language === 'zh' ? '节点接入' : 'Node Access'}
            </div>
            <h1 className="mb-6 text-5xl font-bold md:text-5xl">
              <span className="text-text-primary">{t(language, 'download.title')}</span>
            </h1>
            <p className="mx-auto max-w-3xl text-xl text-text-secondary">
              {t(language, 'download.subtitle')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="mb-12 rounded-3xl p-8 glass-effect"
          >
            <div className="mb-8 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-text-primary">
                  {language === 'zh' ? '跨端节点入口' : 'Cross-end node entries'}
                </h2>
                <p className="mt-2 text-sm text-text-secondary">
                  {language === 'zh'
                    ? '已发布的平台可直接下载，未发布的平台继续展示接入方向。'
                    : 'Platforms with active releases are available for direct download, while unreleased platforms remain roadmap entries.'}
                </p>
              </div>

              {loading ? (
                <div className="inline-flex items-center space-x-2 rounded-full border border-primary/20 px-4 py-2 text-sm text-text-secondary">
                  <Loader className="animate-spin text-primary" size={16} />
                  <span>{t(language, 'download.loading')}</span>
                </div>
              ) : error ? (
                <button
                  onClick={() => {
                    setError(null)
                    setForceRefresh(true)
                  }}
                  className="rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                >
                  {t(language, 'download.retry')}
                </button>
              ) : (
                <div className="inline-flex items-center space-x-2 rounded-full border border-success/20 bg-success/10 px-4 py-2 text-sm font-medium text-success-dark">
                  <CheckCircle size={16} />
                  <span>{t(language, 'download.latestStable')}</span>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-8 rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
                <div className="flex items-start space-x-3">
                  <AlertCircle size={18} className="mt-0.5" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            <div className="grid gap-6 md:grid-cols-2">
              {platformCards.map((platform, index) => {
                const isPlatformReady = Boolean(platform.release?.download_url)
                const downloadButtonLabel = isPlatformReady
                  ? t(language, 'download.downloadNowBtn')
                  : t(language, 'download.comingSoonBtn')

                return (
                  <motion.div
                    key={platform.key}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                    className="flex h-full flex-col rounded-2xl border border-primary/10 p-6 glass-effect"
                  >
                    <div className="mb-5 flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/20 bg-primary/10">
                          <platform.icon size={24} className="text-primary" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-text-primary">{platform.title}</h3>
                          <p className="text-xs uppercase tracking-[0.18em] text-primary">{platform.status}</p>
                        </div>
                      </div>
                    </div>

                    <p className="mb-4 text-sm leading-relaxed text-text-secondary">{platform.summary}</p>
                    <p className="mb-6 flex-1 text-sm leading-relaxed text-text-muted">{platform.detail}</p>

                    {platform.supportsQuickInstall ? (
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          disabled={!isPlatformReady}
                          onClick={() => {
                            if (!isPlatformReady) {
                              return
                            }
                            window.open(platform.release.download_url, '_blank', 'noopener,noreferrer')
                          }}
                          className={isPlatformReady
                            ? 'whitespace-nowrap rounded-xl border border-primary bg-primary px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-dark'
                            : 'cursor-not-allowed whitespace-nowrap rounded-xl border border-border bg-void-lighter px-4 py-3 text-sm font-medium text-text-muted'}
                        >
                          {isPlatformReady ? dualActionDownloadLabel : downloadButtonLabel}
                        </button>
                        <button
                          type="button"
                          onClick={() => setInstallPlatform(platform.key)}
                          className="whitespace-nowrap rounded-xl border border-primary/25 bg-white px-4 py-3 text-sm font-medium text-primary transition-colors hover:border-primary hover:bg-primary/5"
                        >
                          {quickInstallLabel}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={!isPlatformReady}
                        onClick={() => {
                          if (!isPlatformReady) {
                            return
                          }
                          window.open(platform.release.download_url, '_blank', 'noopener,noreferrer')
                        }}
                        className={isPlatformReady
                          ? 'w-full rounded-xl border border-primary bg-primary px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-dark'
                          : 'w-full cursor-not-allowed rounded-xl border border-border bg-void-lighter px-4 py-3 text-sm font-medium text-text-muted'}
                      >
                        {downloadButtonLabel}
                      </button>
                    )}

                    {platform.release && (
                      <div className="mt-4 rounded-xl border border-border bg-void-lighter p-4 text-xs text-text-muted">
                        <div>{t(language, 'download.releaseDate')}: {formatReleaseDate(platform.release.released_at)}</div>
                        <div className="mt-1">{t(language, 'download.fileSize')}: {platform.release.file_size_mb ? `${Number(platform.release.file_size_mb).toFixed(2)} MB` : '--'}</div>
                      </div>
                    )}
                  </motion.div>
                )
              })}
            </div>

            {releaseNotesItems.length > 0 && (
              <div className="mt-8 rounded-2xl p-6 text-left glass-effect">
                <h3 className="mb-3 font-semibold text-text-primary">{t(language, 'download.updateNotes')}</h3>
                <div className="space-y-4">
                  {releaseNotesItems.map((item) => (
                    <div key={item.key}>
                      <div className="mb-1 text-sm font-medium text-text-primary">{item.title}</div>
                      <div
                        className="whitespace-pre-line text-sm text-text-secondary"
                        dangerouslySetInnerHTML={{ __html: item.releaseNotes }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h3 className="mb-6 flex items-center text-2xl font-bold text-text-primary">
              <AlertCircle className="mr-3 text-primary" size={28} />
              {t(language, 'download.requirements.title')}
            </h3>
            <div className="grid gap-6 md:grid-cols-3">
              {requirements.map((requirement, index) => (
                <motion.div
                  key={requirement.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="rounded-xl p-6 glass-effect"
                >
                  <requirement.icon className="mb-4 text-primary" size={32} />
                  <div className="mb-1 text-sm text-text-secondary">{t(language, `download.requirements.${requirement.key}.label`)}</div>
                  <div className="font-semibold text-text-primary">{t(language, `download.requirements.${requirement.key}.value`)}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="mb-6 text-2xl font-bold text-text-primary">{t(language, 'download.steps.title')}</h3>
            <div className="space-y-6">
              {steps.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start space-x-4 rounded-xl p-6 glass-effect"
                >
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-dark to-primary text-lg font-bold text-white">
                    {step.step}
                  </div>
                  <div className="flex-1">
                    <h4 className="mb-1 text-lg font-semibold text-text-primary">{t(language, `download.steps.${step.key}.title`)}</h4>
                    <p className="text-text-secondary">{t(language, `download.steps.${step.key}.desc`)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 rounded-xl p-8 glass-effect"
          >
            <h3 className="mb-4 text-xl font-bold text-text-primary">{t(language, 'download.faq.title')}</h3>
            <div className="space-y-4 text-sm">
              {['q1', 'q2', 'q3', 'q4', 'q5'].map((key) => (
                    <div key={key}>
                      <h4 className="mb-2 font-semibold text-text-primary">{t(language, `download.faq.${key}.q`)}</h4>
                      <p className="text-text-secondary">{t(language, `download.faq.${key}.a`)}</p>
                    </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {activeInstallCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2b1727]/16 px-4 py-8 backdrop-blur-[10px]">
          <div className="relative w-full max-w-3xl overflow-hidden rounded-[32px] border border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(255,248,250,0.94))] text-text-primary shadow-[0_32px_96px_rgba(56,30,48,0.18)]">
            <div className="border-b border-[#f1dde4] bg-[radial-gradient(circle_at_top_left,rgba(255,121,160,0.12),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.9),rgba(255,246,249,0.92))] px-6 py-6 sm:px-8">
              <div className="flex items-start justify-between gap-6">
              <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-primary/80">
                  {language === 'zh' ? '快速安装' : 'Quick Install'}
                  </div>
                  <h3 className="mt-2 text-2xl font-semibold text-text-primary">{activeInstallCard.title}</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-7 text-text-secondary">
                  {activeInstallCard.subtitle}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setInstallPlatform(null)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#f0d7df] bg-white/80 text-text-secondary transition-colors hover:border-primary/30 hover:bg-white"
                  aria-label={language === 'zh' ? '关闭安装弹层' : 'Close install modal'}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="px-6 py-6 sm:px-8">
              <div className="install-command-shell overflow-hidden rounded-[24px] border border-[#f0dce3] bg-[#22151f] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                <div className="flex items-center justify-between border-b border-white/8 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full bg-rose-400/90" />
                    <span className="h-3 w-3 rounded-full bg-amber-300/90" />
                    <span className="h-3 w-3 rounded-full bg-emerald-400/90" />
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleCopyInstallCommand}
                      className="install-command-copy inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/6 px-3 py-1.5 text-[11px] font-medium text-slate-200 transition-colors hover:border-white/20 hover:bg-white/10"
                    >
                      {copyStatus === 'success' ? <Check size={13} /> : <Copy size={13} />}
                      <span>{copyButtonLabel}</span>
                    </button>
                    <div className="text-xs uppercase tracking-[0.22em] text-slate-400">
                      {installPlatform}
                    </div>
                  </div>
                </div>
                <pre className="install-command-code overflow-x-auto px-4 py-5 text-sm leading-7 text-emerald-200 sm:px-5">
                  <code>{activeInstallCard.command}</code>
                </pre>
              </div>

              <div className="mt-5 rounded-[22px] border border-[#f1dde4] bg-[#fff7f9] px-4 py-4 text-sm leading-7 text-text-secondary sm:px-5">
                {activeInstallCard.notes.map((line) => (
                  <div key={line} className="font-mono text-[13px] text-text-secondary">
                    {line}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function releaseNotesFromReleaseMap(releaseMap, language) {
  const items = [
    {
      key: 'windows',
      title: language === 'zh' ? 'Windows 节点' : 'Windows Node',
      releaseNotes: releaseMap?.windows?.release_notes ?? '',
    },
    {
      key: 'linux',
      title: language === 'zh' ? 'Linux 节点' : 'Linux Node',
      releaseNotes: releaseMap?.linux?.release_notes ?? '',
    },
    {
      key: 'android',
      title: language === 'zh' ? 'Android 节点' : 'Android Node',
      releaseNotes: releaseMap?.android?.release_notes ?? '',
    },
    {
      key: 'macos',
      title: language === 'zh' ? 'macOS 节点' : 'macOS Node',
      releaseNotes: releaseMap?.macos?.release_notes ?? '',
    }
  ]

  return items.filter((item) => item.releaseNotes)
}

export default DownloadPage
